import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import {
  CHARGE_STATUS_LABEL, CLAIM_STATUS_LABEL, CLOSURE_STATE_LABEL, COMPONENT_STATUS_LABEL,
  DAMAGE_STATUS_LABEL, DEPOSIT_APPLICATION_LABEL, DEPOSIT_STATE_LABEL, DISPUTE_STATUS_LABEL,
  RELEASE_COMPONENT_LABEL, RESPONSIBILITY_LABEL, blockerText, errorMessage, pesos, tone,
} from "../../lib/rentalLifecycle";

// Task 7: the whole rental on one panel — what still stands between the
// vehicle and the customer, what money is recorded, what cases are open and
// why the rental can or cannot close. Every state is written out; colour only
// repeats what the words already say, and no percentage is ever shown.
const TABS = [
  ["release", "Release"],
  ["money", "Money"],
  ["cases", "Damage & claims"],
  ["closure", "Closure"],
  ["timeline", "Timeline"],
];

function Pill({ status, label }) {
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${tone(status)}`}>
      {label || COMPONENT_STATUS_LABEL[status] || status}
    </span>
  );
}

function Blockers({ items, testId }) {
  if (!items?.length) return null;
  return (
    <ul className="space-y-1 text-sm" data-testid={testId}>
      {items.map((b, i) => (
        <li key={`${b.code}-${i}`} className="flex flex-wrap gap-2">
          <span className="font-medium">{b.severity === "BLOCK" ? "Blocking:" : "Needs review:"}</span>
          <span>{blockerText(b)}</span>
        </li>
      ))}
    </ul>
  );
}

export default function RentalLifecyclePanel({ bookingId }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("release");
  const [timeline, setTimeline] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/rental-lifecycle/bookings/${bookingId}/overview`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [bookingId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab !== "timeline" || timeline) return;
    api.get(`/rental-lifecycle/bookings/${bookingId}/timeline`)
      .then((r) => setTimeline(r.data)).catch(() => setTimeline({ entries: [] }));
  }, [tab, timeline, bookingId]);

  if (!data) return null;
  const caps = data.capabilities || [];
  const can = (cap) => caps.includes(cap);
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setTimeline(null); load(); }
    catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); }
  };

  const release = data.release;
  const closure = data.closure;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4"
             data-testid="rental-lifecycle-panel">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">Rental lifecycle</h2>
        <Pill status={closure.closure_state}
              label={CLOSURE_STATE_LABEL[closure.closure_state] || closure.closure_state} />
      </header>

      <nav className="flex flex-wrap gap-2" role="tablist">
        {TABS.map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key}
                  onClick={() => setTab(key)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium ${
                    tab === key ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-700"}`}
                  data-testid={`rental-tab-${key}`}>
            {label}
          </button>
        ))}
      </nav>

      {tab === "release" && (
        <div className="space-y-3" data-testid="rental-release">
          <p className="text-sm">
            Release readiness: <strong data-testid="rental-release-status">
              {COMPONENT_STATUS_LABEL[release.status] || release.status}</strong>
          </p>
          <ul className="space-y-1">
            {Object.entries(release.components).map(([name, component]) => (
              <li key={name} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span>{RELEASE_COMPONENT_LABEL[name] || name}</span>
                <Pill status={component.status} />
              </li>
            ))}
          </ul>
          <Blockers items={release.blockers} testId="rental-release-blockers" />
          <p className="text-xs text-slate-500">{release.note}</p>
        </div>
      )}

      {tab === "money" && (
        <div className="space-y-3 text-sm" data-testid="rental-money">
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="rounded-lg border border-slate-200 p-3">
              <h3 className="font-medium">Advance payment</h3>
              <p>Received {pesos(release.money.advance.received_centavos)}</p>
              <p className="text-xs text-slate-500">
                Part of the rental price, paid early. Not the security deposit.
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <h3 className="font-medium">Security deposit</h3>
              <p>Received {pesos(release.money.security_deposit.collected_centavos)}</p>
              <p>Applied {pesos(release.money.security_deposit.applied_centavos)}</p>
              <p>Still held {pesos(release.money.security_deposit.held_centavos)}</p>
              <p className="text-xs text-slate-500" data-testid="rental-deposit-state">
                {DEPOSIT_STATE_LABEL[closure.deposit.state] || closure.deposit.state}
                {" — "}{closure.deposit.reason}
              </p>
            </div>
          </div>

          <h3 className="font-medium">Charges</h3>
          {data.charges.length === 0 && <p className="text-slate-600">No charges have been proposed.</p>}
          <ul className="space-y-2">
            {data.charges.map((c) => (
              <li key={c.id} className="rounded-lg border border-slate-200 p-3"
                  data-testid={`rental-charge-${c.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>{c.category_label} — {pesos(c.amount_centavos)}</span>
                  <Pill status={c.status} label={CHARGE_STATUS_LABEL[c.status] || c.status} />
                </div>
                {c.status !== "ESTABLISHED" && (
                  <p className="text-xs text-slate-600">
                    Proposed only. The customer has not been charged this amount yet.
                  </p>
                )}
                {can("rental.charge_establish") && ["AGREED", "PARTIALLY_AGREED",
                  "CUSTOMER_NOTIFIED"].includes(c.status) && (
                  <button type="button" disabled={busy} className="mt-2 text-xs underline"
                          onClick={() => act(() => api.post(`/rental-lifecycle/charges/${c.id}/establish`, {}),
                                             "Charge established")}>
                    Establish this charge
                  </button>
                )}
              </li>
            ))}
          </ul>

          <h3 className="font-medium">Security deposit applications</h3>
          {data.deposit_applications.length === 0 && (
            <p className="text-slate-600">
              None. A deposit can only be applied to a charge that has been established.
            </p>
          )}
          <ul className="space-y-1">
            {data.deposit_applications.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center justify-between gap-2">
                <span>{pesos(a.total_centavos)}</span>
                <Pill status={a.status} label={DEPOSIT_APPLICATION_LABEL[a.status] || a.status} />
              </li>
            ))}
          </ul>

          {data.deposit_refunds.length > 0 && (
            <>
              <h3 className="font-medium">Refunds</h3>
              <ul className="space-y-1">
                {data.deposit_refunds.map((r) => (
                  <li key={r.id} className="flex flex-wrap items-center justify-between gap-2">
                    <span>{pesos(r.amount_centavos)}</span>
                    <Pill status={r.status}
                          label={r.status === "APPROVED" ? "Approved, not yet paid" : "Paid and recorded"} />
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="text-xs text-slate-500">
            ConfirmPH records what the rental business says it received, applied or refunded.
            ConfirmPH does not hold or move the customer&apos;s money.
          </p>
        </div>
      )}

      {tab === "cases" && (
        <div className="space-y-3 text-sm" data-testid="rental-cases">
          <h3 className="font-medium">Damage cases</h3>
          {data.damage_cases.length === 0 && <p className="text-slate-600">No damage cases.</p>}
          <ul className="space-y-2">
            {data.damage_cases.map((c) => (
              <li key={c.id} className="rounded-lg border border-slate-200 p-3"
                  data-testid={`rental-damage-${c.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>{c.description}</span>
                  <Pill status={c.status} label={DAMAGE_STATUS_LABEL[c.status] || c.status} />
                </div>
                <p className="text-xs">
                  Damage confirmed: {c.damage_confirmed ? "Yes" : "Not yet"} · Responsibility:{" "}
                  {RESPONSIBILITY_LABEL[c.responsibility] || c.responsibility}
                </p>
                <p className="text-xs text-slate-500">{c.fault_note}</p>
              </li>
            ))}
          </ul>

          <h3 className="font-medium">Insurance claims</h3>
          {data.claims.length === 0 && <p className="text-slate-600">No claims.</p>}
          <ul className="space-y-1">
            {data.claims.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center justify-between gap-2">
                <span>{c.insurer_name || "Insurer not recorded"} · {c.coverage_type}</span>
                <Pill status={c.status} label={CLAIM_STATUS_LABEL[c.status] || c.status} />
              </li>
            ))}
          </ul>

          <h3 className="font-medium">Disputes</h3>
          {data.disputes.length === 0 && <p className="text-slate-600">No disputes.</p>}
          <ul className="space-y-1">
            {data.disputes.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-2">
                <span>{d.subject_type.replace(/_/g, " ").toLowerCase()}</span>
                <Pill status={d.status} label={DISPUTE_STATUS_LABEL[d.status] || d.status} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === "closure" && (
        <div className="space-y-3 text-sm" data-testid="rental-closure">
          <p>
            Vehicle back and inspected:{" "}
            <strong>{closure.operationally_complete ? "Yes" : "Not yet"}</strong>
          </p>
          <p>
            Money settled: <strong>{closure.financially_closed ? "Yes" : "Not yet"}</strong>
          </p>
          <Blockers items={closure.blockers} testId="rental-closure-blockers" />
          {closure.blockers.length === 0 && (
            <p className="text-slate-600">Nothing is outstanding on this rental.</p>
          )}
          <p className="text-xs text-slate-500">
            Vehicle safety and the rental&apos;s finances are independent: settling the account does
            not clear a safety hold, and an unsettled account does not keep a repaired vehicle off
            the road.
          </p>
          {can("rental.close") && closure.can_close && (
            <button type="button" disabled={busy}
                    className="rounded-lg border border-slate-900 bg-slate-900 px-3 py-1.5 text-xs text-white"
                    data-testid="rental-close-button"
                    onClick={() => act(() => api.post(`/rental-lifecycle/bookings/${bookingId}/close`,
                                                      { reason: "NORMAL_COMPLETION" }),
                                       "Rental closed")}>
              Close this rental
            </button>
          )}
          {can("rental.close") && closure.recorded_closure_state === "CLOSED" && (
            <p className="text-xs text-slate-600">
              Closed. If a toll or a fine arrives later, reopen the rental for reconciliation
              rather than editing its history.
            </p>
          )}
        </div>
      )}

      {tab === "timeline" && (
        <ol className="space-y-2 text-sm" data-testid="rental-timeline">
          {(timeline?.entries || []).map((e, i) => (
            <li key={`${e.ref}-${i}`} className="flex flex-wrap gap-2">
              <span className="text-xs text-slate-500">{(e.at || "").slice(0, 16).replace("T", " ")}</span>
              <span>{e.label}</span>
            </li>
          ))}
          {timeline && timeline.entries.length === 0 && <li className="text-slate-600">Nothing recorded yet.</li>}
        </ol>
      )}
    </section>
  );
}
